import React from 'react';
import { useTranslation } from 'react-i18next';
import { Campaign } from '../../../shared/types/Campaign';
import { TagIcon, PercentIcon, DollarSignIcon } from 'lucide-react';
interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignClick: (campaignId: string) => void;
}
export function CampaignList({
  campaigns,
  onCampaignClick
}: CampaignListProps) {
  const {
    t
  } = useTranslation('shops');
  // Function to calculate budget usage percentage
  const calculateBudgetUsage = (campaign: Campaign) => {
    if (campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  // Function to determine status badge styling
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      expired: 'bg-red-50 text-red-700 border border-red-100'
    };
    const style = styles[status] || styles.inactive;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-10380">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <table className="w-full text-left border-collapse" data-id="element-10381">
      <thead data-id="element-10382">
        <tr className="border-b border-gray-100" data-id="element-10383">
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10384">
            {t('components.CampaignList.campaign')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10385">
            {t('components.CampaignList.code_strategy')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10386">
            {t('components.CampaignList.discount')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10387">
            {t('components.CampaignList.budget_usage')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10388">
            {t('components.CampaignList.expiry_date')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-10389">
            {t('components.CampaignList.status')}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100" data-id="element-10390">
        {campaigns.length > 0 ? campaigns.map(campaign => <tr key={campaign.id} onClick={() => onCampaignClick(campaign.id)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-10391">
              <td className="px-6 py-4" data-id="element-10392">
                <div className="text-sm font-medium text-gray-900 group-hover:text-[#B38A00] transition-colors" data-id="element-10393">
                  {campaign.title}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-10394">
                  {campaign.category}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-10395">
                <div className="text-sm text-gray-900" data-id="element-10396">
                  {campaign.codeStrategy === 'Global' ? <span className="font-medium" data-id="element-10397">{campaign.code}</span> : <span data-id="element-10398">{t('components.CampaignList.generated_codes')}</span>}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-10399">
                  {campaign.codeStrategy}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-10400">
                <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-10401">
                  {campaign.discountPercentage > 0 ? <>
                      <PercentIcon size={14} className="mr-1 text-gray-400" data-id="element-10402" />
                      {campaign.discountPercentage}%
                    </> : <>
                      <DollarSignIcon size={14} className="mr-1 text-gray-400" data-id="element-10403" />
                      {campaign.fixedDiscountAmount} {campaign.currency}
                    </>}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-10404">
                  {t('components.CampaignList.carried_by')}{' '}
                  {campaign.discountCarriedBy}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-10405">
                <div className="flex items-center gap-2" data-id="element-10406">
                  <div className="w-24 bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-10407">
                    <div className="bg-[#FFC70A] h-1.5 rounded-full transition-all" style={{
                width: `${calculateBudgetUsage(campaign)}%`
              }} data-id="element-10408"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-10409">
                    {calculateBudgetUsage(campaign)}%
                  </span>
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-10410">
                  {campaign.budgetUsed.toLocaleString()} /{' '}
                  {campaign.budget.toLocaleString()} {campaign.currency}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-10411">
                <div className="text-sm text-gray-900" data-id="element-10412">
                  {campaign.expiryDate}
                </div>
              </td>
              <td className="px-6 py-4 text-right" data-id="element-10413">
                {getStatusBadge(campaign.status)}
              </td>
            </tr>) : <tr data-id="element-10414">
            <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-10415">
              {t('components.CampaignList.no_campaigns_found_matching_your_criteria')}
            </td>
          </tr>}
      </tbody>
    </table>;
}